import React, {useEffect, useState, Fragment, useRef, memo} from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import _isEqual from 'lodash/isEqual';

import { DSelect, HInput, HCheckbox } from '../../../../validation/HForm';
import { cashBoxService } from '../../../../services/cashbox-service';
import { searchService } from '../../../../services/search-service';
import {addPuritySchemaFunc} from './puritiesFormSchema';

import './puritiesForm.scss';

import DeleteIcon from '../../../../assets/icons/trash.svg';
import {useSelector} from "react-redux";
import BasicAlert from "../../../utilites/Alert/Alert";
import {config} from "../../../../helpers/config";
import {handleKeyPressTA} from "../../../../helpers/common";

const defaultItem = {
  jewel: { value: '', label: '' },
  purity: { value: '', label: '' },
  quantity: 1,
  grossWeight: 0,
  pureWeight: 0,
  puritiesComment: '',
  showComment: false,
  isPart: false,
  additionalInfo: '',
};

const PuritiesForm = memo(({ calculationResult = {}, onUpdate, defaultFormValues, isClear, setContextForm }) => {
  const [jewel, setJewel] = useState([]);
  const [purities, setPurities] = useState([]);
  const [allJewel, setAllJewel] = useState([]);
  const [part, setPart] = useState([]);
  const dataRef = useRef(null);
  const { accessFeatures } = useSelector(({ user }) => user);
  const [errorModalState, setErrorModalState] = useState(false);
  const [charCounts, setCharCounts] = useState({})

  const methods = useForm({
    mode: 'all',
    defaultValues: { purities: defaultFormValues.purities },
    resolver: joiResolver(addPuritySchemaFunc(config().validatePurities)),
  });

  if (setContextForm) {
    setContextForm(methods)
  }
  
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'purities',
    keyName: 'id',
  });

  useEffect(() => {
    if (isClear) {
      methods.setValue('additionalInformation', '')
      remove();
      append(defaultItem, false);
      setCharCounts({})
    }
  }, [isClear, remove, append]);

  useEffect(() => {
    searchService.searchJewels('', 1000).then((response) => {
      setJewel(
        response.data.results.map((v) => ({
          value: parseInt(v.id),
          label: v.name,
          part:v.part,
        })),
      );
      setAllJewel(
        response.data.results.map((v) => ({
          value: parseInt(v.id),
          label: v.name,
          part: v.part,
          synonym: v.synonym
        })),
      );
      setPart(
        response.data.results.map((v) => ({
          value: parseInt(v.id),
          label: v.part,
        })),
      );
    });
    cashBoxService.getCurrentPurities().then((response) => {
      const newPurities = [];
      response.data.purities.forEach((v) => {
        newPurities.push({
          value: v.id,
          label: v.name,
        });
      });
      const sortPurities = newPurities.sort((a, b) =>  {
          if (a.label > b.label) {
            return 1;
          }
          if (a.label < b.label) {
            return -1;
          }
          return 0;
        }
      );
      setPurities(sortPurities);
    });
  }, []);

  useEffect(() => {
    const data = methods.getValues();

    if (!_isEqual(dataRef.current, data) && !_isEqual({}, data)) {
      onUpdate({
        additionalInformation: data.additionalInformation,
        purities: data.purities?.map((item) => {
          if (item.showComment) {
            return item;
          } else {
            return { ...item, puritiesComment: '' };
          }
        }),
      });

      dataRef.current = data;
    }
  }, [fields, onUpdate, methods]);
  const handleGrossWeight = (idx) => {
    const data = methods.getValues().purities[idx];
    const newpureWeight = data.grossWeight === '' ? '0' : data.grossWeight;

    methods.setValue(`purities[${idx}].pureWeight`, newpureWeight, { shouldValidate: true });
    handleChangeContent();
  };

  const handlePureWeight = (idx, calculateDecimals) => {
    const data = methods.getValues().purities[idx];
    const pureWeight = data.pureWeight === '' ? '0' : data.pureWeight;
    const grossWeight = data.grossWeight === '' ? '0' : data.grossWeight;
    const newGrossWeight = +pureWeight > +grossWeight ? pureWeight : grossWeight;

    methods.setValue(`purities[${idx}].grossWeight`, newGrossWeight, { shouldValidate: true });
    if ( +pureWeight < 0 
        && +grossWeight > 0 
        && (+grossWeight) >= (+pureWeight) ) 
    {
      let newPureWeight = ((+grossWeight) + (+pureWeight)).toFixed(calculateDecimals).replace(/\.?0+$/, "")
      newPureWeight = newPureWeight > 0 ? newPureWeight : 0
      methods.setValue(`purities[${idx}].pureWeight`, newPureWeight, { shouldValidate: true });
    }
    handleChangeContent();
  };

  const handlePart = (idx) => {
    if (idx !== undefined) {
      const data = methods.getValues().purities[idx];

      const newIsPart =data.isPart;

      const newJewel = jewel?.filter(jewel => jewel.label === data.jewel.label || jewel.part === data.jewel.label).map(j => {
        if (newIsPart) {
          return {value: j.value, label: j.part}
        }
        return {value: j.value, label: j.label}
      })
      methods.setValue(`purities[${idx}].jewel`, newJewel[0], {shouldValidate: true});
      showComment()
    }
  }

  const handleChangeContent = (idx) => {
    const data = methods.getValues();
    if ( isFinite(idx) ) {
    const purity = data.purities[idx].purity.label;
    let grossWeight = data.purities[idx].grossWeight;
    let pureWeight = data.purities[idx].pureWeight;

    if (  purity.startsWith('Серебро') 
          && ( grossWeight.lastIndexOf('.') !== ( grossWeight.length - 2 ) ) 
          && ( grossWeight.lastIndexOf('.') !== -1 )
    ) {
      grossWeight = grossWeight.slice(0, grossWeight.length - 1)
      methods.setValue(`purities[${idx}].grossWeight`, grossWeight, { shouldValidate: true });
    } 
    if (  purity.startsWith('Серебро') 
          && ( pureWeight.lastIndexOf('.') !== ( pureWeight.length - 2 ) ) 
          && ( pureWeight.lastIndexOf('.') !== -1 )
    ) {
      pureWeight = pureWeight.slice(0, pureWeight.length-1)
      methods.setValue(`purities[${idx}].pureWeight`, pureWeight, { shouldValidate: true });
    }
    fields[idx].purity = data.purities[idx].purity
    }

    if (!_isEqual(dataRef.current, data)) {
      onUpdate({
        additionalInformation: data.additionalInformation,
        purities: data.purities?.map((item) => {
          if (item.showComment) {
            return item;
          } else {
            return { ...item, puritiesComment: '' };
          }
        }),
      });

      dataRef.current = data;
    }
  };

  const addPurityRow = () => {
    append(defaultItem, false);
  };
  const checkValidLength = () => {
    if (accessFeatures['purities.count_contracts']) {
      if (fields.length < 10) {
        addPurityRow()
      } else {
        setErrorModalState(true);
      }
    } else {
      addPurityRow()
    }
  }

  const removePurityRow = (index) => {
    remove(index);
  };

  const handleJewelChange = (val, action) => {
    if (action.action === 'input-change') {
      const matchingItems = allJewel.filter(obj => {
        return Object.values(obj).some(value =>
          typeof value === 'string' && value.includes(val)
        );
      });
      setJewel(
        matchingItems.map(({value, label, part}) => ({
          value, label, part
        })),
      );
      setPart(
        matchingItems.map(({value, part}) => ({
          value,
          label: part,
        })),
      );
    }
  }

  const showComment = () => {
    const newPurities = methods.getValues().purities;

    methods.setValue('purities', newPurities);
  };

  const onFocusChange = (idx, type) =>{
    const data = methods.getValues().purities[idx];
    if (data){
      if (type === 'grossWeight') {
        const newGrossWeight = data.grossWeight === '0' ? '' : data.grossWeight
        methods.setValue(`purities[${idx}].grossWeight`, newGrossWeight);
      }
      if (type === 'pureWeight') {
        const newpureWeight = data.pureWeight === '0' ? '' : data.pureWeight
        methods.setValue(`purities[${idx}].pureWeight`, newpureWeight);
      }
    }
  }

  const handleCharCountChange = (idx)=> {
    if (!accessFeatures['count_additionalInfo_symbols']) {
      return
    }
    const data = methods.getValues();
    let additionalInfo = data.purities[idx].additionalInfo
    setCharCounts((prevCharCounts) => ({
      ...prevCharCounts,
      [idx]: additionalInfo.length,
    }));
  }

  return (
    <FormProvider {...methods}>
      <form>
        <fieldset className="p-b-0">
          <div className="content-section-title">
            <div className="content-section-title-actionitems nostretch">
              <h2>Залоговая ценность</h2>
            </div>
          </div>
        </fieldset>
        <div className="content-datatable">
          <div className="content-datatable-content">
            <table className="table hover">
              <thead>
                <tr>
                  <th scope="col">Название</th>
                  <th scope="col">Проба</th>
                  <th scope="col">Общий вес, г.</th>
                  <th scope="col">Лигатурный вес, г.</th>
                  <th scope="col">Количество</th>
                  <th scope="col" colSpan="2">
                    Оценочная
                    <br /> стоимость, руб.
                  </th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, idx) => {
                  const purityLabel = methods.watch(`purities[${idx}].purity`)?.label
                  const calculateDecimals = purityLabel && purityLabel.startsWith('Серебро') ? 1 : 2;
                  return (
                    <Fragment key={field.id}>
                      <tr>
                        <td className="purities-form-max">
                          <span className="caption">Название</span>
                          <div className="table-cellcontent">
                            <div className="form-element flex-direction-row">
                              <div className='purities-form-max-numbering'>{idx+1}</div>
                              <div className="form-element-data combobox">
                                <DSelect
                                  h="true"
                                  name={`purities[${idx}].jewel`}
                                  defaultValue={field.jewel || ''}
                                  options={field.isPart ? part : jewel}
                                  handleOptions={handleJewelChange}
                                  handleChangeContent={handleChangeContent}
                                  isLoading={false}
                                  placeholder="Название"
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="caption">Проба</span>
                          <div className="table-cellcontent">
                            <div className="form-element">
                              <div className="form-element-data form-element-md combobox-md">
                                <DSelect
                                  h="true"
                                  name={`purities[${idx}].purity`}
                                  defaultValue={field.purity}
                                  handleChangeContent={() => handleChangeContent(idx)}
                                  options={purities}
                                  isSearchable={true}
                                  isLoading={false}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="caption">Общий вес, г.</span>
                          <div className="table-cellcontent">
                            <div className="form-element">
                              <div className="form-element-data number">
                                <HInput
                                  h="true"
                                  name={`purities[${idx}].grossWeight`}
                                  defaultValue={field.grossWeight}
                                  onBlur={() => handleGrossWeight(idx)}
                                  className="input"
                                  type="text"
                                  decimals={calculateDecimals}
                                  onFocus={()=>onFocusChange(idx, 'grossWeight')}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="caption">Лигатурный вес, г.</span>
                          <div className="table-cellcontent">
                            <div className="form-element">
                              <div className="form-element-data number">
                                <HInput
                                  h="true"
                                  name={`purities[${idx}].pureWeight`}
                                  defaultValue={field.pureWeight}
                                  onBlur={() => handlePureWeight(idx, calculateDecimals)}
                                  className="input"
                                  type="text"
                                  decimals={calculateDecimals}
                                  minus={true}
                                  onFocus={()=>onFocusChange(idx, 'pureWeight')}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="caption">Количество</span>
                          <div className="table-cellcontent">
                            <div className="form-element">
                              <div className="form-element-data number">
                                <HInput
                                  h="true"
                                  name={`purities[${idx}].quantity`}
                                  defaultValue={field.quantity}
                                  onChange={handleChangeContent}
                                  className="input"
                                  type="text"
                                  decimals={0}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="purities-form-min">
                          <span className="caption">
                            Оценочная
                            <br />
                            стоимость, руб.
                          </span>
                          <p>{calculationResult?.estimatedValue[idx]}</p>
                        </td>
                        <td>
                          <div className="table-cell-actions">
                            <button
                              type="button"
                              className="button-icontrigger"
                              onClick={() => {
                                if (fields.length !== 1) {
                                  removePurityRow(idx);
                                }
                              }}>
                              <span className="icon">
                                <DeleteIcon />
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={accessFeatures['purities.additional_info'] ? "2" : "7" }>
                          <div className="form-element flex-direction-row m-none">
                            <div className="form-element-data">
                              <HCheckbox
                                h="true"
                                name={`purities[${idx}].isPart`}
                                className="input"
                                defaultValue={field.isPart}
                                onChange={()=>handlePart(idx)}
                              />
                            </div>
                            <label className="form-element-label m-r-spacer" htmlFor="isPart">
                              Часть
                            </label>
                            <div className="form-element-data">
                              <HCheckbox
                                h="true"
                                name={`purities[${idx}].showComment`}
                                className="input"
                                defaultValue={field.showComment}
                                onChange={showComment}
                              />
                            </div>
                            <label className="form-element-label m-r-spacer" htmlFor="showComment">
                              Вставки:
                            </label>
                            <div
                              className={`form-element-data textarea full-width ${
                                field.showComment ? '' : 'd-none'
                              }`}>
                              <HInput
                                h="true"
                                className="input full-width"
                                rows="1"
                                cols="10"
                                name={`purities[${idx}].puritiesComment`}
                                defaultValue={field.puritiesComment}
                                onBlur={handleChangeContent}
                              />
                            </div>
                          </div>
                        </td>
                        {accessFeatures['purities.additional_info'] &&
                          <td colSpan="5">
                            <div className="form-element flex-direction-row m-none">
                              <label className="form-element-label m-r-spacer" htmlFor="additionalInfo">
                                Доп. описание
                              </label>
                              <div className={`form-element-data textarea full-width`}>
                                <HInput
                                  h="true"
                                  className="input full-width"
                                  rows="1"
                                  cols="10"
                                  name={`purities[${idx}].additionalInfo`}
                                  defaultValue={field.additionalInfo}
                                  onBlur={handleChangeContent}
                                  onChange={()=>handleCharCountChange(idx)}
                                  onKeyDown={config().validatePurities ?
                                      (e) => handleKeyPressTA(e) : null}
                                />
                                {
                                  accessFeatures['count_additionalInfo_symbols'] &&
                                    <span>{charCounts[idx] !== undefined ? charCounts[idx] : field.additionalInfo.length} /20 </span>
                                }
                              </div>
                            </div>
                          </td>
                        }
                      </tr>
                    </Fragment>
                  );
                })}
                <tr>
                  <td colSpan={4}></td>
                  <td colSpan={3}>
                    <div className='purities-addButton'>
                      <button
                          className="action-item"
                          type="button"
                          onClick={checkValidLength}
                      >
                        Добавить ценность
                      </button>
                    </div>
                  </td>
                </tr>
                {accessFeatures['deal.additional_info'] &&
                  <tr>
                    <td colSpan={7}>
                    <label className="form-element-label m-r-spacer" htmlFor="showComment">
                      Добавить комментарий
                    </label>
                    <HInput
                        h="true"
                        className="input full-width purities-additionalInformation"
                        rows="1"
                        cols="11"
                        name="additionalInformation"
                        defaultValue={defaultFormValues.additionalInformation}
                        onBlur={handleChangeContent}
                    />
                    </td>
                  </tr>
                }
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="2">
                    <p>Итого</p>
                  </td>
                  <td colSpan="1">
                    <span className="caption">Общий вес, г.</span>
                    <p>{calculationResult.totalGrossWeight}</p>
                  </td>
                  <td colSpan="1">
                    <span className="caption">Лигатурный вес, г.</span>
                    <p>{calculationResult.totalPureWeight}</p>
                  </td>
                  <td colSpan="1">
                    <span className="caption">Количество</span>
                    <p>{calculationResult.totalQuantity}</p>
                  </td>
                  <td colSpan="2">
                    <span className="caption">
                      Оценочная
                      <br />
                      стоимость, руб.
                    </span>
                    <p>{ calculationResult.totalEstimatedValue !==0 ? calculationResult.totalEstimatedValue.toFixed(2) : 0 }</p>
                  </td>
                </tr>
              </tfoot>
            </table>
            <BasicAlert onClose={() => setErrorModalState(false)}
                        message="Максимальное количество ценностей 10"
                        isOpen={errorModalState}
                        isError={true}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
});

export default PuritiesForm;
